/**
 * Knowledge queries.
 *
 * The catalogue exposes a document DETAIL endpoint (no list), a gaps list +
 * detail, and a search endpoint. There is no `GET /documents` list, so
 * `useKnowledgeDocument(id)` is the document read.
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type Faq,
  faqSchema,
  type KnowledgeCategory,
  type KnowledgeDocument,
  type KnowledgeGap,
  type KnowledgeSearchResult,
  knowledgeDocumentSchema,
  knowledgeGapSchema,
  knowledgeSearchResultSchema,
  type Paginated,
  paginated,
} from '../responses';
import { unwrap } from './_helpers';

export interface FaqsFilters {
  category?: KnowledgeCategory;
  active?: boolean;
  limit?: number;
  offset?: number;
}

/** `GET /knowledge/faqs` — paginated FAQ entries (active and inactive) for the hotel. */
export function useFaqs(
  tenantId: string,
  hotelId: string,
  filters: FaqsFilters = {},
): UseQueryResult<Paginated<Faq>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.faqs(tenantId, hotelId, filters as Record<string, unknown>),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/faqs',
        {
          params: { path: { tenantId, hotelId }, query: filters },
          parseAs: 'json',
        },
      );
      return unwrap(result, paginated(faqSchema));
    },
  });
}

/** `GET /knowledge/documents/:documentId`. */
export function useKnowledgeDocument(
  tenantId: string,
  hotelId: string,
  documentId: string,
): UseQueryResult<KnowledgeDocument> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.knowledgeDocument(tenantId, hotelId, documentId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}',
        { params: { path: { tenantId, hotelId, documentId } }, parseAs: 'json' },
      );
      return unwrap(result, knowledgeDocumentSchema);
    },
    enabled: documentId.length > 0,
  });
}

/** `GET /knowledge/gaps` — open knowledge gaps for the hotel. */
export function useKnowledgeGaps(
  tenantId: string,
  hotelId: string,
): UseQueryResult<KnowledgeGap[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.knowledgeGaps(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps',
        { params: { path: { tenantId, hotelId } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(knowledgeGapSchema));
    },
  });
}

/** `GET /knowledge/gaps/:gapId`. */
export function useKnowledgeGap(
  tenantId: string,
  hotelId: string,
  gapId: string,
): UseQueryResult<KnowledgeGap> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.knowledgeGap(tenantId, hotelId, gapId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps/{gapId}',
        { params: { path: { tenantId, hotelId, gapId } }, parseAs: 'json' },
      );
      return unwrap(result, knowledgeGapSchema);
    },
    enabled: gapId.length > 0,
  });
}

/** `GET /knowledge/search?query=&limit=` — retrieval over the hotel knowledge base. */
export function useKnowledgeSearch(
  tenantId: string,
  hotelId: string,
  query: string,
  limit?: number,
): UseQueryResult<KnowledgeSearchResult[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.knowledgeSearch(tenantId, hotelId, query, limit),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/search',
        { params: { path: { tenantId, hotelId }, query: { query, limit } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(knowledgeSearchResultSchema));
    },
    enabled: query.length > 0,
  });
}
